import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import { Button } from "ui/button";
import { Plus, Trash2, X } from "lucide-react";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";

export interface PassportDetails {
  id: number;
  passportNameEn: string;
  passportNameAr: string;
  passportNo: string;
  issueDate: string;
  expiryDate: string;
}

export interface VisaDetails {
  id: number;
  visaType: string;
  visaNumber: string;
  visaFolderNo: string;
  issuedFrom: string;
  issuedDate: string;
  validity: string;
}

export interface FamilyMember {
  id: number;
  relation: string;
  itsId: string;
  name: string;
  dob: string;
  occupation: string;
  staying: string;
  age?: string;
}

interface PassportFamilyTabProps {
  isViewMode?: boolean;
  passportDetails: PassportDetails[];
  setPassportDetails: React.Dispatch<React.SetStateAction<PassportDetails[]>>;
  visaDetails: VisaDetails[];
  setVisaDetails: React.Dispatch<React.SetStateAction<VisaDetails[]>>;
  savePassport: (passportData: PassportDetails) => Promise<void>;
  saveVisa: (visaData: VisaDetails) => Promise<void>;
  familyMembers: FamilyMember[];
  setFamilyMembers: React.Dispatch<React.SetStateAction<FamilyMember[]>>;
  saveFamilyMember: (familyData: FamilyMember) => Promise<void>;
  deleteFamilyMember: (id: number) => Promise<void>;
}

export default function PassportFamilyTab({
  passportDetails,
  setPassportDetails,
  visaDetails,
  setVisaDetails,
  savePassport,
  saveVisa,
  familyMembers,
  setFamilyMembers,
  saveFamilyMember,
  deleteFamilyMember,
  isViewMode = false,
}: PassportFamilyTabProps) {
  const [showPassportDialog, setShowPassportDialog] = useState(false);
  const [showVisaDialog, setShowVisaDialog] = useState(false);
  const [showFamilyDialog, setShowFamilyDialog] = useState(false);
  const [familyMemberErrors, setFamilyMemberErrors] = useState<
    Record<string, string>
  >({});
  const [passportErrors, setPassportErrors] = useState<Record<string, string>>(
    {},
  );
  const [isDeleteFamilyDialogOpen, setIsDeleteFamilyDialogOpen] =
    useState(false);
  const [deletingFamilyMember, setDeletingFamilyMember] =
    useState<FamilyMember | null>(null);

  const [newPassport, setNewPassport] = useState<PassportDetails>({
    id: Date.now(),
    passportNameEn: "",
    passportNameAr: "",
    passportNo: "",
    issueDate: "",
    expiryDate: "",
  });

  const [newVisa, setNewVisa] = useState<VisaDetails>({
    id: Date.now(),
    visaType: "",
    visaNumber: "",
    visaFolderNo: "",
    issuedFrom: "",
    issuedDate: "",
    validity: "",
  });

  const [newFamilyMember, setNewFamilyMember] = useState<FamilyMember>({
    id: Date.now(),
    relation: "",
    itsId: "",
    name: "",
    dob: "",
    occupation: "",
    staying: "",
  });

  const addPassport = async () => {
    const errors: Record<string, string> = {};
    if (!newPassport.passportNo.trim()) {
      errors.passportNo = "Passport Number is required";
    }

    if (!newPassport.expiryDate) {
      errors.expiryDate = "Expiry Date is required";
    }
    setPassportErrors(errors);
    if (Object.keys(errors).length > 0) return;
    await savePassport(newPassport);
    setNewPassport({
      id: Date.now(),
      passportNameEn: "",
      passportNameAr: "",
      passportNo: "",
      issueDate: "",
      expiryDate: "",
    });
    setShowPassportDialog(false);
  };

  const addVisa = async () => {
    await saveVisa(newVisa);

    setNewVisa({
      id: Date.now(),
      visaType: "",
      visaNumber: "",
      visaFolderNo: "",
      issuedFrom: "",
      issuedDate: "",
      validity: "",
    });

    setShowVisaDialog(false);
  };

  const addFamilyMember = async () => {
    const errors: Record<string, string> = {};
    if (!newFamilyMember.relation) {
      errors.relation = "Relation is required";
    }
    if (!newFamilyMember.itsId) {
      errors.itsId = "ITS Id is required";
    }
    if (!newFamilyMember.name) {
      errors.name = "Name is required";
    }
    if (!newFamilyMember.dob) {
      errors.dob = "Date of Birth is required";
    }
    setFamilyMemberErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    await saveFamilyMember(newFamilyMember);
    setNewFamilyMember({
      id: Date.now(),
      relation: "",
      itsId: "",
      name: "",
      dob: "",
      occupation: "",
      staying: "",
    });
    setFamilyMemberErrors({});
    setShowFamilyDialog(false);
  };

  const confirmDeleteFamilyMember = async () => {
    if (!deletingFamilyMember) return;
    await deleteFamilyMember(deletingFamilyMember.id);
    setIsDeleteFamilyDialogOpen(false);
    setDeletingFamilyMember(null);
  };

  return (
    <div className="space-y-6">
      {/* PASSPORT */}
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Passport Details</CardTitle>
        </CardHeader>

        <CardContent>
          <Table>
            <TableHeader>
              <TableRow
                style={{
                  backgroundColor: "#E3F2FD",
                }}
              >
                <TableHead>Passport Name (English)</TableHead>
                <TableHead>Passport Name (Arabic)</TableHead>
                <TableHead>
                  Passport Number <span className="text-red-500">*</span>
                </TableHead>
                <TableHead>Issue Date</TableHead>
                <TableHead>
                  Expiry Date <span className="text-red-500">*</span>
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {passportDetails.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-6 text-gray-500 text-sm"
                  >
                    No passport details added yet
                  </TableCell>
                </TableRow>
              ) : (
                passportDetails.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.passportNameEn}</TableCell>
                    <TableCell>{item.passportNameAr}</TableCell>
                    <TableCell>{item.passportNo}</TableCell>
                    <TableCell>{item.issueDate}</TableCell>
                    <TableCell>{item.expiryDate}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!isViewMode && (
            <Button
              className="mt-4"
              variant="outline"
              size="sm"
              onClick={() => setShowPassportDialog(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Passport
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Visa */}
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Visa Details</CardTitle>
        </CardHeader>

        <CardContent>
          <Table>
            <TableHeader>
              <TableRow
                style={{
                  backgroundColor: "#E3F2FD",
                }}
              >
                <TableHead>Visa Type</TableHead>
                <TableHead>Visa Number</TableHead>
                <TableHead>Folder Number</TableHead>
                <TableHead>Issued From</TableHead>
                <TableHead>Issued Date</TableHead>
                <TableHead>Validity</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {visaDetails.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-6 text-gray-500 text-sm"
                  >
                    No visa details added yet
                  </TableCell>
                </TableRow>
              ) : (
                visaDetails.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.visaType}</TableCell>
                    <TableCell>{item.visaNumber}</TableCell>
                    <TableCell>{item.visaFolderNo}</TableCell>
                    <TableCell>{item.issuedFrom}</TableCell>
                    <TableCell>{item.issuedDate}</TableCell>
                    <TableCell>{item.validity}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {!isViewMode && (
            <Button
              className="mt-4"
              variant="outline"
              size="sm"
              onClick={() => setShowVisaDialog(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Visa
            </Button>
          )}
        </CardContent>
      </Card>

      {/* FAMILY */}
      <Card className="bg-white" style={{ gap: "10px" }}>
        <CardHeader>
          <CardTitle>Family Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow
                  style={{
                    backgroundColor: "#E3F2FD",
                  }}
                >
                  <TableHead className="text-xs">
                    Relation <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead className="text-xs">
                    ITS Id <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead className="text-xs">
                    Name <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead className="text-xs">
                    DOB <span className="text-red-500">*</span>
                  </TableHead>
                  <TableHead className="text-xs">Age</TableHead>
                  <TableHead className="text-xs">Occupation</TableHead>
                  <TableHead className="text-xs">Staying</TableHead>
                  {!isViewMode && (
                    <TableHead className="text-xs">Action</TableHead>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody>
                {familyMembers.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-6 text-gray-500 text-sm"
                    >
                      No family members added yet
                    </TableCell>
                  </TableRow>
                ) : (
                  familyMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.relation}</TableCell>
                      <TableCell>{member.itsId}</TableCell>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.dob}</TableCell>
                      <TableCell>{member.age}</TableCell>
                      <TableCell>{member.occupation}</TableCell>
                      <TableCell>{member.staying}</TableCell>
                      {!isViewMode && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDeletingFamilyMember(member);
                              setIsDeleteFamilyDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {!isViewMode && (
            <Button
              className="mt-4"
              variant="outline"
              size="sm"
              onClick={() => setShowFamilyDialog(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Family Member
            </Button>
          )}
        </CardContent>
      </Card>

      {/* PASSPORT DIALOG */}
      {showPassportDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold" style={{ color: "#5B9BD5" }}>
                Add Passport Details
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPassportDialog(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* BODY */}
            {/* PASSPORT DETAILS */}
            <div>
              <div className="p-4 space-y-4">
                {/* Passport Name EN */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Passport Name (English)"
                    required
                    id="passportNameEn"
                    value={newPassport.passportNameEn}
                    onChange={(e) =>
                      setNewPassport({
                        ...newPassport,
                        passportNameEn: e.target.value,
                      })
                    }
                    placeholder="Enter passport name in English"
                  />

                  {passportErrors.passportNameEn && (
                    <p className="text-red-500 text-xs mt-1">
                      {passportErrors.passportNameEn}
                    </p>
                  )}
                </div>

                {/* Passport Name AR */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Passport Name (Arabic)"
                    id="passportNameAr"
                    value={newPassport.passportNameAr}
                    onChange={(e) =>
                      setNewPassport({
                        ...newPassport,
                        passportNameAr: e.target.value,
                      })
                    }
                    placeholder="Enter passport name in Arabic"
                  />
                </div>
                {/* Passport No */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Passport Number"
                    required
                    id="passportNo"
                    value={newPassport.passportNo}
                    onChange={(e) =>
                      setNewPassport({
                        ...newPassport,
                        passportNo: e.target.value,
                      })
                    }
                    placeholder="Passport number"
                  />

                  {passportErrors.passportNo && (
                    <p className="text-red-500 text-xs mt-1">
                      {passportErrors.passportNo}
                    </p>
                  )}
                </div>
                {/* Issue Date */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Issue Date"
                    type="date"
                    id="issueDate"
                    value={newPassport.issueDate}
                    onChange={(e) =>
                      setNewPassport({
                        ...newPassport,
                        issueDate: e.target.value,
                      })
                    }
                  />
                </div>
                {/* Expiry Date */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Expiry Date"
                    required
                    type="date"
                    id="expiryDate"
                    value={newPassport.expiryDate}
                    onChange={(e) =>
                      setNewPassport({
                        ...newPassport,
                        expiryDate: e.target.value,
                      })
                    }
                  />

                  {passportErrors.expiryDate && (
                    <p className="text-red-500 text-xs mt-1">
                      {passportErrors.expiryDate}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* FOOTER */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowPassportDialog(false)}
              >
                Cancel
              </Button>

              <Button
                style={{ background: "#5B9BD5" }}
                className="text-white"
                onClick={addPassport}
              >
                Add Passport
              </Button>
            </div>
          </div>
        </div>
      )}

      {showVisaDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold" style={{ color: "#5B9BD5" }}>
                Add Visa Details
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVisaDialog(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* BODY */}
            <div className="p-4 space-y-4">
              {/* Visa Type */}
              <div className="space-y-2">
                <SelectFieldset label="Visa Type">
                  <Select
                    value={newVisa.visaType}
                    onValueChange={(value) =>
                      setNewVisa({
                        ...newVisa,
                        visaType: value,
                      })
                    }
                  >
                    <SelectTriggerUI>
                      <SelectValue placeholder="Select visa type" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Residence Permit">
                        Residence Permit
                      </SelectItem>
                      <SelectItem value="Business">Business</SelectItem>
                      <SelectItem value="Tourist">Tourist</SelectItem>
                      <SelectItem value="Zyarat">Zyarat</SelectItem>
                      <SelectItem value="Iqama">Iqama</SelectItem>
                      <SelectItem value="Multiple Entry">
                        Multiple Entry
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {passportErrors.visaType && (
                  <p className="text-red-500 text-xs mt-1">
                    {passportErrors.visaType}
                  </p>
                )}
              </div>
              {/* Visa Number */}
              <div className="space-y-2">
                <InputFieldset
                  label="Visa Number"
                  id="visaNumber"
                  value={newVisa.visaNumber}
                  onChange={(e) =>
                    setNewVisa({
                      ...newVisa,
                      visaNumber: e.target.value,
                    })
                  }
                  placeholder="Visa number"
                />
                {passportErrors.visaNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {passportErrors.visaNumber}
                  </p>
                )}
              </div>
              {/* Folder No */}
              <div className="space-y-2">
                <InputFieldset
                  label="Visa Folder Number"
                  id="visaFolderNo"
                  value={newVisa.visaFolderNo}
                  onChange={(e) =>
                    setNewVisa({
                      ...newVisa,
                      visaFolderNo: e.target.value,
                    })
                  }
                  placeholder="Folder number"
                />
              </div>
              {/* Issued From */}
              <div className="space-y-2">
                <InputFieldset
                  label="Issued From"
                  id="issuedFrom"
                  value={newVisa.issuedFrom}
                  onChange={(e) =>
                    setNewVisa({
                      ...newVisa,
                      issuedFrom: e.target.value,
                    })
                  }
                  placeholder="Issued from"
                />
              </div>
              {/* Issued Date */}
              <div className="space-y-2">
                <InputFieldset
                  label="Issued Date"
                  type="date"
                  id="issuedDate"
                  value={newVisa.issuedDate}
                  onChange={(e) =>
                    setNewVisa({
                      ...newVisa,
                      issuedDate: e.target.value,
                    })
                  }
                />
              </div>
              {/* Validity */}
              <div className="space-y-2">
                <InputFieldset
                  label="Validity"
                  id="validity"
                  value={newVisa.validity}
                  onChange={(e) =>
                    setNewVisa({
                      ...newVisa,
                      validity: e.target.value,
                    })
                  }
                  placeholder="e.g., 2 Years"
                />
              </div>
            </div>
            {/* FOOTER */}
            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowVisaDialog(false)}
              >
                Cancel
              </Button>

              <Button
                style={{ background: "#5B9BD5" }}
                className="text-white"
                onClick={addVisa}
              >
                Add Visa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FAMILY DIALOG */}
      {showFamilyDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold" style={{ color: "#5B9BD5" }}>
                Add Family Member
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFamilyDialog(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <SelectFieldset label="Relation" required>
                  <Select
                    value={newFamilyMember.relation}
                    onValueChange={(value) => {
                      setNewFamilyMember({
                        ...newFamilyMember,
                        relation: value,
                      });
                      setFamilyMemberErrors({
                        ...familyMemberErrors,
                        relation: "",
                      });
                    }}
                  >
                    <SelectTriggerUI
                      id="relation"
                      className={
                        familyMemberErrors.relation
                          ? "border-red-500 border-1"
                          : ""
                      }
                    >
                      <SelectValue placeholder="Select relation" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="Spouse">Spouse</SelectItem>
                      <SelectItem value="Son">Son</SelectItem>
                      <SelectItem value="Daughter">Daughter</SelectItem>
                      <SelectItem value="Father">Father</SelectItem>
                      <SelectItem value="Mother">Mother</SelectItem>
                      <SelectItem value="Brother">Brother</SelectItem>
                      <SelectItem value="Sister">Sister</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {familyMemberErrors.relation && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {familyMemberErrors.relation}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="ITS Id"
                  required
                  id="familyItsId"
                  value={newFamilyMember.itsId}
                  onChange={(e) => {
                    setNewFamilyMember({
                      ...newFamilyMember,
                      itsId: e.target.value,
                    });
                    setFamilyMemberErrors({ ...familyMemberErrors, itsId: "" });
                  }}
                  placeholder="e.g., 10345678"
                  className={
                    familyMemberErrors.itsId ? "border-red-500 border-1" : ""
                  }
                />
                {familyMemberErrors.itsId && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {familyMemberErrors.itsId}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Name"
                  required
                  id="familyName"
                  value={newFamilyMember.name}
                  onChange={(e) => {
                    setNewFamilyMember({
                      ...newFamilyMember,
                      name: e.target.value,
                    });
                    setFamilyMemberErrors({ ...familyMemberErrors, name: "" });
                  }}
                  placeholder="Full name"
                  className={
                    familyMemberErrors.name ? "border-red-500 border-1" : ""
                  }
                />
                {familyMemberErrors.name && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {familyMemberErrors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Date of Birth"
                  required
                  type="date"
                  id="familyDob"
                  value={newFamilyMember.dob}
                  onChange={(e) => {
                    setNewFamilyMember({
                      ...newFamilyMember,
                      dob: e.target.value,
                    });
                    setFamilyMemberErrors({ ...familyMemberErrors, dob: "" });
                  }}
                  className={
                    familyMemberErrors.dob ? "border-red-500 border-1" : ""
                  }
                />
                {familyMemberErrors.dob && (
                  <p className="text-red-500 text-xs font-medium mt-1">
                    {familyMemberErrors.dob}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Occupation"
                  id="familyOccupation"
                  value={newFamilyMember.occupation}
                  onChange={(e) =>
                    setNewFamilyMember({
                      ...newFamilyMember,
                      occupation: e.target.value,
                    })
                  }
                  placeholder="e.g., Teacher"
                />
              </div>
              <div className="space-y-2">
                <InputFieldset
                  label="Staying (City)"
                  id="familyStaying"
                  value={newFamilyMember.staying}
                  onChange={(e) =>
                    setNewFamilyMember({
                      ...newFamilyMember,
                      staying: e.target.value,
                    })
                  }
                  placeholder="e.g., Najaf"
                />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-4 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFamilyDialog(false)}
              >
                Cancel
              </Button>
              <Button
                style={{ background: "#5B9BD5" }}
                className="text-white"
                size="sm"
                onClick={addFamilyMember}
              >
                Add Member
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FAMILY DELETE DIALOG */}
      <Dialog
        open={isDeleteFamilyDialogOpen}
        onOpenChange={setIsDeleteFamilyDialogOpen}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              Delete Family Member
            </DialogTitle>

            <DialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold">
                "{deletingFamilyMember?.name}"
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteFamilyDialogOpen(false);
                setDeletingFamilyMember(null);
              }}
            >
              No, Cancel
            </Button>

            <Button
              onClick={confirmDeleteFamilyMember}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Yes, Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
